import { defineStore } from "pinia";
import { customApi } from "@app/helpers";
import { wpRestNonce } from "@services/authService"; // Assuming this provides the nonce

export const useDashboardStore = defineStore("dashboard", {
  state: () => ({
    userAppointments: {
      upcoming: [],
      past: [],
    },
    currentAppointmentDetails: null,
    isLoadingAppointments: false,
    isLoadingDetails: false,
    errorAppointments: null,
    errorDetails: null,
  }),

  actions: {
    async fetchUserAppointments() {
      this.isLoadingAppointments = true;
      this.errorAppointments = null;
      try {
        const response = await customApi.get("/user-appointments", {
          headers: {
            "X-WP-Nonce": wpRestNonce.value, // Make sure wpRestNonce is available and reactive if needed
          },
        });
        this.userAppointments.upcoming = response.data?.upcoming || [];
        this.userAppointments.past = response.data?.past || [];
        console.log(
          "DashboardStore: User appointments fetched",
          this.userAppointments
        );
      } catch (error) {
        console.error(
          "DashboardStore: Failed to fetch user appointments:",
          error
        );
        this.errorAppointments =
          "Could not load your appointments. Please refresh.";
        // Optionally re-throw or handle more specifically
      } finally {
        this.isLoadingAppointments = false;
      }
    },

    async fetchSingleAppointmentDetails(bookingId) {
      if (!bookingId) {
        this.errorDetails = "Booking ID is missing.";
        console.error(
          "DashboardStore: fetchSingleAppointmentDetails called without bookingId."
        );
        return;
      }
      this.isLoadingDetails = true;
      this.errorDetails = null;
      this.currentAppointmentDetails = null; // Reset previous details

      try {
        // The endpoint was defined as /booking-details/{booking_id}
        // Ensure your customApi helper can handle path parameters or construct the URL directly.
        // If customApi.get automatically handles it like /booking-details/TJ123, this is fine.
        // Otherwise, you might need: customApi.get(`/booking-details/${bookingId}`)
        const response = await customApi.get(`/booking-details/${bookingId}`, {
          headers: {
            // Nonce might not be strictly needed for GET but good for consistency if backend checks
            "X-WP-Nonce": wpRestNonce.value,
          },
        });
        this.currentAppointmentDetails = response.data;
        console.log(
          "DashboardStore: Single appointment details fetched",
          this.currentAppointmentDetails
        );
      } catch (error) {
        console.error(
          `DashboardStore: Failed to fetch appointment details for ${bookingId}:`,
          error
        );
        this.errorDetails =
          "Could not load appointment details. Please try again.";
        if (error.response && error.response.status === 404) {
          this.errorDetails = "Appointment not found.";
        } else if (error.response && error.response.status === 403) {
          this.errorDetails =
            "You do not have permission to view this appointment.";
        }
        // Optionally re-throw
      } finally {
        this.isLoadingDetails = false;
      }
    },

    async updateBookingDetails(bookingId, detailsToUpdate) {
      // detailsToUpdate should be an object like:
      // { appointment_status: 'cancelled' }
      // or { appointment_date: 'YYYY-MM-DD', appointment_time: 'HH:MM' }
      this.isLoadingDetails = true; // Can use a general loading state or a specific one for updates
      this.errorDetails = null;
      try {
        const config = {
          headers: {
            "X-WP-Nonce": wpRestNonce.value, // Make sure wpRestNonce is available
          },
        };
        // Assuming your customApi.post can handle the full URL if needed,
        // or that the base URL is configured in customApi.
        const response = await customApi.post(
          `/booking-update/${bookingId}`,
          detailsToUpdate,
          config
        );

        if (response.data.success) {
          // Update the local store if the currentAppointmentDetails matches the updated one
          if (this.currentAppointmentDetails?.booking_id === bookingId) {
            this.currentAppointmentDetails = {
              ...this.currentAppointmentDetails,
              ...response.data.updated_fields, // Merge updated fields from backend response
            };
          }
          // Optionally, re-fetch all appointments if a status change might affect lists
          // await this.fetchUserAppointments(); // Uncomment if needed
          return response.data; // Return success response
        } else {
          // Handle cases where backend returns success:false or specific error message
          throw new Error(response.data.message || "Failed to update booking.");
        }
      } catch (error) {
        this.errorDetails =
          error.response?.data?.message || // Prefer backend error message
          error.message || // Axios error message or custom error message
          "Failed to update booking details.";
        console.error("DashboardStore: updateBookingDetails error", error);
        throw error; // Re-throw to be caught by the component for UI feedback (e.g., toast)
      } finally {
        this.isLoadingDetails = false;
      }
    },

    clearAppointmentsData() {
      this.userAppointments = { upcoming: [], past: [] };
      this.currentAppointmentDetails = null;
      this.errorAppointments = null;
      this.errorDetails = null;
      // This might be called on user logout, for example.
    },
  },
});
